import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminAfasTransactionInnerDetails from './AdminAfasTransactionInnerDetails';
import { authHeaders } from '../utils/apiAuth';

const AdminAfasTransactionDetails = ({ t, language }) => {
  const { voucherNr } = useParams();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [journal2ViewMode, setJournal2ViewMode] = useState('grid');

  const apiUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch transaction
        const trxRes = await fetch(`${apiUrl}/afas/transactions/${voucherNr}`, { headers: authHeaders() });
        if (!trxRes.ok) {
          throw new Error('Failed to fetch AFAS transaction details');
        }
        const trxData = await trxRes.json();
        setTransaction(trxData);
        let oData = null;

        // Fetch order details
        const orderRes = await fetch(`${apiUrl}/api/orders/${voucherNr}`, { headers: authHeaders() });
        if (orderRes.ok) {
          oData = await orderRes.json();
          setOrderData(oData);
        }
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (voucherNr) {
      fetchData();
    }
  }, [voucherNr, apiUrl]);

  if (loading) {
    return (
      <div className="p-4 sm:p-8 space-y-8 animate-pulse">
        {/* Header Skeleton */}
        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 mb-8">
          <div>
            <div className="h-4 bg-gray-200 rounded w-32 mb-4"></div>
            <div className="flex items-center">
              <div className="h-10 w-10 bg-gray-200 rounded-full mr-3 shrink-0"></div>
              <div className="h-8 bg-gray-200 rounded w-64"></div>
            </div>
            <div className="h-4 bg-gray-200 rounded w-48 mt-3 ml-11"></div>
          </div>
        </div>

        {/* Content Skeleton */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden p-6">
          {/* Order Info Skeleton */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <div className="h-4 bg-gray-200 rounded w-40"></div>
              <div className="h-8 bg-gray-200 rounded w-24"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="p-4 rounded-lg border border-gray-100 bg-gray-50 flex flex-col h-20 justify-between">
                  <div className="h-3 bg-gray-200 rounded w-16"></div>
                  <div className="h-5 bg-gray-200 rounded w-24"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Cart Items Skeleton */}
          <div className="mb-8">
            <div className="h-4 bg-gray-200 rounded w-32 mb-4"></div>
            <div className="h-32 bg-gray-100 rounded-lg w-full border border-gray-200"></div>
          </div>

          {/* Journals Skeleton */}
          <div className="space-y-6">
            <div className="h-40 bg-gray-100 rounded-lg w-full border border-gray-200"></div>
            <div className="h-40 bg-gray-100 rounded-lg w-full border border-gray-200"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !transaction) {
    return (
      <div className="p-4 sm:p-8">
        <div className="text-red-600 font-medium mb-4">{t?.error_title || 'Error'}: {error || 'Transaction not found'}</div>
        <button onClick={() => navigate(-1)} className="text-primary hover:underline">
          &larr; {t?.back || 'Back'}
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 space-y-8">
      {/* Header */}
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-500 hover:text-primary transition-colors text-sm font-semibold mb-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t?.back_to_transactions || 'Back to transactions'}
          </button>
          <h2 className="text-3xl font-bold text-gray-800 flex items-center shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-3 bg-primary text-white rounded-full p-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {transaction.VoucherNr}
            {orderData?.recreatedFrom && (
              <span className="ml-4 text-sm font-semibold text-rose-500 border border-rose-200 bg-rose-50 px-2.5 py-1 rounded-md">
                recreated from : {orderData.recreatedFrom}
              </span>
            )}
          </h2>
          {transaction.lastSyncedAt && (
            <p className="text-gray-500 text-sm mt-1 ml-11">
              {t?.last_synced_at || 'Last Synced'}: {new Date(transaction.lastSyncedAt).toLocaleString(language === 'nl' ? 'nl-NL' : 'en-US')}
            </p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6">
          <AdminAfasTransactionInnerDetails
            trx={transaction}
            orderData={orderData}
            journal2ViewMode={journal2ViewMode}
            setJournal2ViewMode={setJournal2ViewMode}
            apiUrl={apiUrl}
            language={language}
            t={t}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminAfasTransactionDetails;
